import * as React from 'react';
import { useField } from 'react-final-form';
import { Select, InputTextWithAddon, FormGroup, InputText } from '@gandi/form';
import { ToggleFieldGroup, TogglePillField } from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';
import { Base, IconButton } from '@gandi/ui';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var styles = {
  "address": "WebForwardForm-address_OGxdf",
  "addressHost": "WebForwardForm-addressHost_Eo0fL"
};
const FQDN_EXAMPLE = 'example.com';
const variant = ['block'];
const computeError = (...errors) => {
  const concrete = errors.filter(Boolean);
  if (concrete.length === 0) {
    return undefined;
  }
  if (concrete.length === 1) {
    return concrete[0];
  }
  return /*#__PURE__*/_jsx("ul", {
    children: concrete.map(error => /*#__PURE__*/_jsx("li", {
      children: error
    }, error))
  });
};
const AddressFieldsGroup = ({
  forwardField
}) => {
  const {
    __
  } = useTranslator();
  const sourceProtocolName = `${forwardField.prefix}.sourceProtocol`;
  const sourceProtocol = useField(sourceProtocolName);
  const hostName = `${forwardField.prefix}.host`;
  const host = useField(hostName);
  const error = computeError(sourceProtocol.meta.error, host.meta.error);
  const showError = error && (sourceProtocol.meta.touched || host.meta.touched);
  return /*#__PURE__*/_jsx(FormGroup, {
    label: __('Address'),
    name: hostName,
    error: showError ? error : undefined,
    help: /*#__PURE__*/_jsx("span", {
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML: __('You can use * to forward all subdomains, including www.<br/>' + 'You can leave it empty to forward just the bare domain (without the www or other subdomain).', {
        withHTML: true
      })
    }),
    children: /*#__PURE__*/_jsxs(Base, {
      className: styles.address,
      marginBottom: "xsmall",
      children: [/*#__PURE__*/_jsx(Base, {
        marginRight: "xsmall",
        children: /*#__PURE__*/_jsx(Select, {
          id: sourceProtocolName,
          required: true,
          variant: variant,
          "aria-label": __('Source protocol'),
          values: [{
            value: 'http',
            text: 'http://'
          }, {
            value: 'httpsonly',
            text: 'https://'
          }, {
            value: 'https',
            text: 'http:// + https://'
          }],
          ...sourceProtocol.input
        })
      }), /*#__PURE__*/_jsx(Base, {
        className: styles.addressHost,
        children: /*#__PURE__*/_jsx(InputTextWithAddon, {
          id: hostName,
          placeholder: __('example'),
          rightAddon: `.${FQDN_EXAMPLE}`,
          ...host.input
        })
      })]
    })
  });
};
const ForwardToFieldsGroup = ({
  forwardField
}) => {
  const {
    __
  } = useTranslator();
  const targetProtocolName = `${forwardField.prefix}.targetProtocol`;
  const targetProtocol = useField(targetProtocolName);
  const targetUrlName = `${forwardField.prefix}.targetUrl`;
  const targetUrl = useField(targetUrlName);
  const error = computeError(targetProtocol.meta.error, targetUrl.meta.error);
  const showError = error && (targetProtocol.meta.touched || targetUrl.meta.touched);
  return /*#__PURE__*/_jsx(FormGroup, {
    label: __('Address to forward to'),
    name: targetUrlName,
    error: showError ? error : undefined,
    help: __('You should put the address that you want to forward to in the field above. Note that the current nameservers will be kept with this web forwarding. To use Gandi\'s nameservers, complete the section "Add Nameservers & DNS records".'),
    children: /*#__PURE__*/_jsxs(Base, {
      className: styles.address,
      marginBottom: "xsmall",
      children: [/*#__PURE__*/_jsx(Base, {
        marginRight: "xsmall",
        children: /*#__PURE__*/_jsx(Select, {
          id: targetProtocolName,
          required: true,
          variant: variant,
          "aria-label": __('Target protocol'),
          values: [{
            value: 'http://',
            text: 'http://'
          }, {
            value: 'https://',
            text: 'https://'
          }, {
            value: 'ftp://',
            text: 'ftp://'
          }],
          ...targetProtocol.input
        })
      }), /*#__PURE__*/_jsx(Base, {
        className: styles.addressHost,
        children: /*#__PURE__*/_jsx(InputText, {
          id: targetUrlName,
          required: true,
          ...targetUrl.input
        })
      })]
    })
  });
};
const WebForwardForm = ({
  forwardField,
  onDeleteWebForward,
  removable
}) => {
  const {
    __
  } = useTranslator();
  const typeName = `${forwardField.prefix}.type`;
  const isHttps = forwardField.forward.sourceProtocol !== 'http';
  const typeOptions = [{
    label: __('Permanent'),
    value: 'http301'
  }, {
    label: __('Temporary'),
    value: 'http302'
  }];
  if (!isHttps) {
    typeOptions.push({
      label: __('Masked'),
      value: 'cloak'
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [removable && /*#__PURE__*/_jsxs(Base, {
      flex: true,
      children: [/*#__PURE__*/_jsxs(Base, {
        flexAuto: true,
        weight: "semibold",
        flex: true,
        flexAlignItemsCenter: true,
        children: [/*#__PURE__*/_jsx("span", {
          "aria-hidden": true,
          children: "+ "
        }), __('Add a forwarding')]
      }), /*#__PURE__*/_jsx(IconButton, {
        type: "button",
        icon: "trash",
        shape: "circle",
        intent: "danger",
        kind: "outline",
        small: true,
        onClick: () => onDeleteWebForward(forwardField),
        children: __('Delete')
      })]
    }), /*#__PURE__*/_jsx(Base, {
      marginTop: removable ? 'small' : undefined,
      marginBottom: "small",
      children: /*#__PURE__*/_jsx(AddressFieldsGroup, {
        forwardField: forwardField
      })
    }), /*#__PURE__*/_jsx(Base, {
      marginBottom: "small",
      children: /*#__PURE__*/_jsx(ForwardToFieldsGroup, {
        forwardField: forwardField
      })
    }), /*#__PURE__*/_jsx(ToggleFieldGroup, {
      name: typeName,
      required: true,
      label: __('Type of forwarding'),
      help: /*#__PURE__*/_jsx("span", {
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML: __('The target address will be shown to the user.<br/>' + 'Permanent web forwarding has good search-engine ranking.', {
          withHTML: true
        })
      }),
      children: typeOptions.map(({
        label,
        value
      }) => /*#__PURE__*/_jsx(TogglePillField, {
        name: typeName,
        value: value,
        children: label
      }, value))
    })]
  });
};
export default WebForwardForm;